<template>
    <div class="text-center">
        <template v-if="state === 'waiting'">
            <i class="glyphicon glyphicon-refresh glyphicon-spin"></i>
        </template>
        <template v-else-if="state === 'syncing'">
            <SyncProgressBar :text="progressBarText"
                             :percent="progressBarPercent" />
        </template>
        <template v-else-if="state === 'synced'">
            <SyncResults :user="syncUser"
                         :friend-counts="friendCounts"
                         :sync-results="syncResults" />
        </template>
        <template v-else-if="state === 'failed'">
            <div class="alert alert-info">
                <i class="glyphicon glyphicon-info-sign"></i>
                We are aware of some recent changes Facebook has made which are affecting some users' ability to check
                their friends.
                <br />
                If you are using Chrome, make sure you are using the latest version of the Who Deleted Me browser
                extension, which contains a fix for this. The latest version is <strong>3.0.14</strong>.
                <br />
                We are still working on an update to the Firefox extension to fix this issue. In the meantime you
                can consider using the Chrome extension in a supported browser.
                <br />
                We are sorry for the inconvenience and appreciate your patience.
            </div>

            <div class="alert alert-danger">
                <h3 class="mb-0">
                    {{ errorTitle }}
                </h3>

                <p v-if="errorDescription"
                   class="mt-05">
                    {{ errorDescription }}
                </p>

                <p v-if="errorDetail"
                   class="mt-05">
                    Error details:
                </p>
                <pre v-if="errorDetail">{{ errorDetail }}</pre>

                <a v-if="showGetExtensionButtonInError"
                   href="/"
                   class="btn btn-info">
                    Get The Browser Extension From The Home Page
                </a>
            </div>

            <div class="actions mt-1">
                <a class="recheckButton btn btn-info"
                   href="/sync"><i class="glyphicon glyphicon-refresh"></i> Ty Again</a>
                &nbsp;
                <a class="nextButton btn btn-blue"
                   href="/friends/deleted-me"><i class="glyphicon glyphicon-chevron-right"></i> See Who Deleted You
                    Previously</a>
            </div>
        </template>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import SyncProgressBar from '@/app/pages/sync/SyncProgressBar';
import { finalStateMessages, syncErrorMessages, syncProgressMessages } from '@/app/pages/sync/sync';
import { timeoutPromise } from '@/app/functions';
import SyncResults from '@/app/pages/sync/SyncResults';
import { dispatchExtensionEvent } from '@/app/lib/Functions/extension';
import MessageType from 'wdm-common/Messaging/MessageType';

export default {
    components: { SyncResults, SyncProgressBar },

    data() {
        return {
            state: 'waiting',

            progressBarText: null,
            progressBarPercent: 0,

            syncUser: null,
            syncResults: null,
            friendCounts: null,

            errorTitle: null,
            errorDescription: null,
            errorDetail: null,

            /**
             * Keep track of if we have received any response from the extension.
             * If not and there is a timeout they probably don't have the extension.
             */
            hasResponseFromExtension: false,
            showGetExtensionButtonInError: false,
        };
    },

    computed: {
        ...mapState(['extensionVersion']),
    },

    created() {
        // No need to worry about tearing down these event listeners because
        // we are an MPA not an SPA.

        document.addEventListener(MessageType.IS_SYNCING_FRIENDS, (e) => {
            this.hasResponseFromExtension = true;
            this.resetTimeoutTimeout();

            this.state = 'syncing';

            const syncState = e.detail.state;
            const syncStateData = e.detail.data || {};

            // eslint-disable-next-line prefer-const
            let { percent, text } = syncProgressMessages[syncState];

            // Special message handling for some states...
            switch (syncState) {
                case 'FETCH_FRIENDS':
                    text = 'Downloading friends';
                    if (syncStateData.loaded) {
                        text += ' (' + syncStateData.loaded + ')';
                    }
                    text += ' (this may take a few seconds)...';

                    if (syncStateData.loaded && syncStateData.loaded >= 200) {
                        text += '<br/><small>(This may take longer average than usual because you have lots of friends!)</small>';
                    }
                    break;
            }

            this.progressBarText = text;
            this.progressBarPercent = percent;
        });

        document.addEventListener(MessageType.DID_SYNC_FRIENDS, async (e) => {
            this.hasResponseFromExtension = true;
            this.clearTimeoutTimeout();

            // Show a funny message for 500ms.
            if (this.state === 'syncing') {
                this.progressBarText = finalStateMessages[Math.floor(Math.random() * finalStateMessages.length)] + '...';
                this.progressBarPercent = 95;
                await timeoutPromise(500);
            }

            // Show finished for 100ms.
            this.progressBarText = 'Finished!';
            this.progressBarPercent = 100;
            await timeoutPromise(100);

            // Now switch to the results.
            this.syncUser = e.detail.user;
            if (e.detail.syncResults) {
                // v3.0.13+ of the extension sends syncResults.
                this.syncResults = e.detail.syncResults;
                this.friendCounts = e.detail.friendCounts;
            } else {
                this.friendCounts = e.detail.results;
                this.syncResults = {
                    fetchedActiveFriends: e.detail.results.activeFriends,
                    fetchedDeactivatedFriends: 0,
                };
            }
            this.state = 'synced';
        });

        document.addEventListener(MessageType.FAILED_SYNCING_FRIENDS, (e) => {
            this.hasResponseFromExtension = true;
            this.clearTimeoutTimeout();

            console.log('WdmExtFailedSyncingFriends', e.detail);
            this.onError(
                e.detail.type || e.detail.errorCode || null,
                e.detail.message,
            );
        });

        // Sync at startup.
        // Set a timeout to handle stuck syncing.
        setTimeout(() => {
            this.startSync();
        }, 1000);
    },

    methods: {
        startSync() {
            dispatchExtensionEvent(MessageType.SHOULD_SYNC_FRIENDS);
            this.resetTimeoutTimeout();
        },

        resetTimeoutTimeout() {
            this.clearTimeoutTimeout();
            this.syncTimeoutTimeout = setTimeout(() => {
                if (this.hasResponseFromExtension) {
                    this.onError('TIMEOUT');
                } else {
                    this.onError('NO_EXTENSION');
                }
            }, 7000);
        },

        clearTimeoutTimeout() {
            if (this.syncTimeoutTimeout) {
                clearTimeout(this.syncTimeoutTimeout);
            }
        },

        onError(errorType, errorMessage = null) {
            console.log('onError', errorType, errorMessage);
            this.state = 'failed';
            this.errorTitle = syncErrorMessages[errorType]?.title || 'Something Went Wrong';
            this.errorDescription = syncErrorMessages[errorType]?.desc;
            this.errorDetail = errorMessage;
            this.showGetExtensionButtonInError = !!syncErrorMessages[errorType]?.showGetExtensionButton;
        },
    },
};
</script>
